import { MixContractHoldSide, MixContractMarginMode } from 'constants/MixContractGlobal'

import { calOpenCost } from 'helper/formula/MixContractFormula'
import { selectorWithoutCache as selector } from 'util/RecoilWithoutCache'
import StatisticResult from 'util/StatisticResult'
import {
    ContractPositionLevelList,
    CurrentBusinessLineContractListState,
    getLevel,
    getMixContractInfoBySymbolId
} from './Contract'
import {
    formatCurrentOrders,
    MixContractCurrentOrderFixedData,
    MixContractCurrentOrdersState
} from './Order'
import { MixContractBaseFixedPositionListSelector } from './Position'
import { getContractConfig } from './PositionComputed'
import { getTokenPriceByTokenId } from './Token'
import delegateType2Holdside from 'helper/delegateType2Holdside'
import Decimal from 'decimal.js'
import {
    createEmptyMixTickerInfo,
    MixContractAllTickersMap,
    MixContractTickerKeyMap
} from './Ticker'

/**
 * 计算现有委托的多头 空头保证金 以及总保证金 以及币对价值
 *
 * 返回的函数
 * @returns {
 *    symbolIdTotalLongOrderMargin 单币对多头委托保证金 uniKey
 *    symbolIdTotalShortOrderMargin 单币对空头委托保证金 uniKey
 *    symbolIdTotalLongPosMargin 单币对多头仓位保证金 uniKey
 *    symbolIdTotalShortPosMargin 单币对空头仓位保证金（dMargin） uniKey
 *    symbolIdCurTotalLongPosMargin 单币对当前多头仓位保证金 uniKey
 *    symbolIdCurTotalShortPosMargin 单币对当前空头仓位保证金（dMargin4CalcCanOpen） uniKey
 *    symbolIdTotalAllMaxValue 所有币对占用价值 uniKey
 *    symbolIdTotalMargin 单币对占用保证金 uniKey
 *    tokenIdFixedTotalValue 所有逐仓币对当前价值*(MM+爆仓手续费) uniKey
 *    tokenIdTotalValue 所有币对占用保证金  usdt 对应 btc eth eos
 *    tokenIdTotalMargin 所有币对占用保证金  usdt 对应 btc eth eos
 *    tokenIdFixedTotalMargin 所有逐仓币对占用保证金 uniKey
 *    symbolIdTotalLongPosNum 单币对多头仓位数量 uniKey
 *    symbolIdTotalShortPosNum 单币对空头仓位数量 uniKey
 *    }
 * */
export const MixOneWayCalculateOrdersMargin = selector({
    key: 'mix-one-way-calculate-order-selector',
    get: ({ get }) => {
        const contractList = get(CurrentBusinessLineContractListState)
        // const orders = get(MixContractCurrentTpslOrdersState) // 所有止盈止损委托
        const curTargetOrder = get(MixContractCurrentOrdersState) // 当前委托 普通委托  没有 planType 字段
        const positionList = get(MixContractBaseFixedPositionListSelector) //所有仓位
        const tickers = get(MixContractAllTickersMap)
        const tickerKeyMap = get(MixContractTickerKeyMap)
        //所有的委托 包含止盈止损委托 不区分全仓逐仓
        let allOrderList: MixContractCurrentOrderFixedData[] = formatCurrentOrders(
            get,
            curTargetOrder
        )

        // 单币对多头仓位保证金 uniKey
        let symbolIdTotalLongPosMargin = new StatisticResult()
        // 单币对空头仓位保证金 uniKey
        let symbolIdTotalShortPosMargin = new StatisticResult()

        // 单币对多头委托保证金 uniKey
        let symbolIdTotalLongOrderMargin = new StatisticResult()

        // 单币对空头委托保证金 uniKey
        let symbolIdTotalShortOrderMargin = new StatisticResult()

        // 单币对多头仓位数量
        let symbolIdTotalLongPosNum = new StatisticResult()
        // 单币对空头仓位数量
        let symbolIdTotalShortPosNum = new StatisticResult()

        // 单币对当前多头仓位保证金
        let symbolIdCurTotalLongPosMargin = new StatisticResult()

        // 单币对当前空头仓位保证金
        let symbolIdCurTotalShortPosMargin = new StatisticResult()

        // 所有币对占用保证金  usdt 对应 btc eth eos
        let tokenIdTotalMargin = new StatisticResult()

        // 单币对多头仓位保证金 逐仓
        let fixedSymbolIdTotalLongPosMargin = new StatisticResult()

        // 单币对空头仓位保证金 逐仓
        let fixedSymbolIdTotalShortPosMargin = new StatisticResult()

        // 单币对多头委托保证金 逐仓
        let fixedSymbolIdTotalLongOrderMargin = new StatisticResult()

        // 单币对空头委托保证金 逐仓
        let fixedSymbolIdTotalShortOrderMargin = new StatisticResult()

        // 所有逐仓币对占用仓位保证金 所有逐仓币对占用保证金
        let tokenIdFixedTotalMargin = new StatisticResult()

        // 单币对占用保证金  btc 或者 eth 或者 eos
        let symbolIdTotalMargin = new StatisticResult()

        //  临时装载占用保证金
        let tokenSymbolNetValueMap = new Map<string, Set<string>>()

        //  临时装载币对价值
        let SymbolNetValueMap = new Map<string, Set<string>>()

        // 单币对多头仓位价值 //全仓
        let symbolIdTotalLongPosValue = new StatisticResult()
        // 单币对多头仓位价值 //逐仓
        let symbolIdTotalFixedLongPosValue = new StatisticResult()

        // 单币对多头仓位价值 专门用来取mm
        let symbolIdTotalLongPosValueForMM = new StatisticResult()
        // 单币对空头仓位价值 专门用来取mm
        let symbolIdTotalShortPosValueForMM = new StatisticResult()
        // 单币对空头仓位价值
        let symbolIdTotalShortPosValue = new StatisticResult()
        // 单币对空头仓位价值
        let symbolIdTotalFixedShortPosValue = new StatisticResult()

        // 单币对多头委托价值 只算全仓不算逐仓
        let symbolIdTotalLongOrderValue = new StatisticResult()
        // 单币对空头委托价值 只算全仓不算逐仓
        let symbolIdTotalShortOrderValue = new StatisticResult()

        // 单币对多头委托价值 只算逐仓不算全仓
        let symbolIdTotalFixedLongOrderValue = new StatisticResult()
        // 单币对空头委托价值 只算逐仓不算全仓
        let symbolIdTotalFixedShortOrderValue = new StatisticResult()

        // 币对当前价值
        // 所有全仓币对当前价值*(MM+爆仓手续费) 不包含逐仓
        let tokenIdTotalValue = new StatisticResult()

        // 币对占用价值
        // 逐仓也在里面
        let symbolIdTotalAllMaxValue = new StatisticResult()

        // 币对占用价值 的大头
        let tokenIdTotalValueMax = new StatisticResult()

        // 单币对 逐仓多头委托价值
        let symbolIdFixedTotalLongOrderValue = new StatisticResult()
        // 单币对 逐仓空头委托价值
        let symbolIdFixedTotalShortOrderValue = new StatisticResult()
        // 币对逐仓委托价值大头
        let tokenIdFixedTotalValue = new StatisticResult()

        let totalSymbols = new Set<string>()

        allOrderList.forEach(item => {
            let contractConfig = get(getMixContractInfoBySymbolId(item.symbolId))
            let bp = get(getTokenPriceByTokenId(item.tokenId)).indexPrice
            //止盈止损委托是没有 dealCount 字段 所以可以用这种方法去掉
            if (!item.onlyLow && item.dealCount) {
                let cost = calOpenCost(
                    item.delegatePrice || 0,
                    item.delegateCount - item.dealCount,
                    bp,
                    item.leverage,
                    contractConfig.openCostUpRatio,
                    contractConfig.feeRate,
                    contractConfig.feeRateUpRatio
                )
                const unionKey = `${item.tokenId}_${item.symbolId}`
                let delegateUsdtValue = new Decimal(item.delegateCount || 0)
                    .sub(item.dealCount)
                    .times(item.delegatePrice || 0)
                //不区分全仓逐仓
                if (
                    delegateType2Holdside(item.delegateType) === MixContractHoldSide.LONG_POSITIONS
                ) {
                    symbolIdTotalLongOrderMargin.add(unionKey, cost)
                    if (item.marginMode !== MixContractMarginMode.FIXED_MARGIN) {
                        //只算全仓 不算逐仓
                        symbolIdTotalLongOrderValue.add(unionKey, delegateUsdtValue)
                    } else {
                        fixedSymbolIdTotalLongOrderMargin.add(unionKey, cost)
                    }
                    symbolIdTotalFixedLongOrderValue.add(unionKey, delegateUsdtValue)
                } else {
                    symbolIdTotalShortOrderMargin.add(unionKey, cost)
                    //只算全仓 不算逐仓
                    if (item.marginMode !== MixContractMarginMode.FIXED_MARGIN) {
                        symbolIdTotalShortOrderValue.add(unionKey, delegateUsdtValue)
                    } else {
                        fixedSymbolIdTotalShortOrderMargin.add(unionKey, cost)
                    }
                    symbolIdTotalFixedShortOrderValue.add(unionKey, delegateUsdtValue)
                }
                //逐仓
                if (item.marginMode === MixContractMarginMode.FIXED_MARGIN && item.delegatePrice) {
                    if (
                        delegateType2Holdside(item.delegateType) ===
                        MixContractHoldSide.LONG_POSITIONS
                    ) {
                        symbolIdFixedTotalLongOrderValue.add(unionKey, delegateUsdtValue)
                    } else {
                        symbolIdFixedTotalShortOrderValue.add(unionKey, delegateUsdtValue)
                    }
                }
                totalSymbols.add(item.symbolId)
                tokenSymbolNetValueMap.set(item.tokenId, totalSymbols)
                SymbolNetValueMap.set(item.tokenId, totalSymbols)
            }
        })

        //
        for (let position of positionList) {
            const {
                tokenId,
                symbolId,
                available,
                locked,
                dMargin,
                holdSide,
                dMargin4CalcCanOpen,
                averageOpenPrice
            } = position
            let target = tickers.get(tickerKeyMap.get(symbolId))
            let template = createEmptyMixTickerInfo()

            if (!target) {
                target = template
            } else {
                target = Object.assign(template, target)
            }

            const unionKey = `${tokenId}_${symbolId}`
            const fairPrice = target.markPrice
            const holdUsdtValue = new Decimal(available).add(locked).times(fairPrice) // 仓位 最新标记价格计算的 价值
            const minPrice = Decimal.min(fairPrice, averageOpenPrice)
            const holdUsdtValueForMM = new Decimal(available).add(locked).times(minPrice)
            //这里只算全仓 不包含逐仓
            if (position.marginMode !== MixContractMarginMode.FIXED_MARGIN) {
                if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
                    symbolIdTotalLongPosValue.add(unionKey, holdUsdtValue)
                    symbolIdTotalLongPosValueForMM.add(unionKey, holdUsdtValueForMM)
                } else {
                    symbolIdTotalShortPosValue.add(unionKey, holdUsdtValue)
                    symbolIdTotalShortPosValueForMM.add(unionKey, holdUsdtValueForMM)
                }
            }
            // 全部的仓位价值 包含逐仓 全仓
            // 逐仓 单币对仓位占用保证金计算
            if (position.marginMode === MixContractMarginMode.FIXED_MARGIN) {
                if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
                    symbolIdTotalFixedLongPosValue.add(unionKey, holdUsdtValue)
                    fixedSymbolIdTotalLongPosMargin.add(unionKey, dMargin)
                } else {
                    symbolIdTotalFixedShortPosValue.add(unionKey, holdUsdtValue)
                    fixedSymbolIdTotalShortPosMargin.add(unionKey, dMargin)
                }
            }

            if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
                symbolIdTotalLongPosMargin.add(unionKey, dMargin)
                symbolIdCurTotalLongPosMargin.add(unionKey, dMargin4CalcCanOpen)
                symbolIdTotalLongPosNum.add(unionKey, available)
            } else {
                symbolIdTotalShortPosMargin.add(unionKey, dMargin)
                symbolIdCurTotalShortPosMargin.add(unionKey, dMargin4CalcCanOpen)
                symbolIdTotalShortPosNum.add(unionKey, available)
            }
            totalSymbols.add(symbolId)
            tokenSymbolNetValueMap.set(tokenId, totalSymbols)
            SymbolNetValueMap.set(tokenId, totalSymbols)
        }
        //保证金
        tokenSymbolNetValueMap.forEach((symbols, tokenId) => {
            for (let item of symbols) {
                const unionKey = `${tokenId}_${item}`
                let longPos = symbolIdTotalLongPosMargin.get(unionKey)
                let shortPos = symbolIdTotalShortPosMargin.get(unionKey)
                let longOrder = symbolIdTotalLongOrderMargin.get(unionKey)
                let shortOrder = symbolIdTotalShortOrderMargin.get(unionKey)
                tokenIdTotalMargin.add(
                    tokenId,
                    Math.max(longPos.add(longOrder).toNumber(), shortPos.add(shortOrder).toNumber())
                )
                symbolIdTotalMargin.set(
                    unionKey,
                    Math.max(longPos.add(longOrder).toNumber(), shortPos.add(shortOrder).toNumber())
                )
                let longFixedOrder = symbolIdFixedTotalLongOrderValue.get(unionKey)
                let shortFixedOrder = symbolIdFixedTotalShortOrderValue.get(unionKey)
                tokenIdFixedTotalValue.add(
                    tokenId,
                    Math.max(longFixedOrder.toNumber(), shortFixedOrder.toNumber())
                )

                //新增 逐仓仓位占用保证金
                let marginLongPos = fixedSymbolIdTotalLongPosMargin.get(unionKey)
                let marginShortPos = fixedSymbolIdTotalShortPosMargin.get(unionKey)
                let marginLongOrder = fixedSymbolIdTotalLongOrderMargin.get(unionKey)
                let marginShortOrder = fixedSymbolIdTotalShortOrderMargin.get(unionKey)
                tokenIdFixedTotalMargin.add(
                    tokenId,
                    Math.max(
                        marginLongPos.add(marginLongOrder).toNumber(),
                        marginShortPos.add(marginShortOrder).toNumber()
                    )
                )
            }
        })
        //这里有两处都要同步做修改 Account.ts
        //币对价值
        SymbolNetValueMap.forEach((symbols, tokenId) => {
            for (let item of symbols) {
                const unionKey = `${tokenId}_${item}`
                let longPos = symbolIdTotalLongPosValue.get(unionKey)
                let shortPos = symbolIdTotalShortPosValue.get(unionKey)
                let longOrder = symbolIdTotalLongOrderValue.get(unionKey)
                let shortOrder = symbolIdTotalShortOrderValue.get(unionKey)
                //币对当前价值=max(多头仓位价值+多头委托价值, 空头仓位价值+空头委托价值)
                let final = Math.max(
                    longPos.add(longOrder).toNumber(),
                    shortPos.add(shortOrder).toNumber()
                )

                // 取mm
                let longPosForMM = symbolIdTotalLongPosValueForMM.get(unionKey)
                let shortPosForMM = symbolIdTotalShortPosValueForMM.get(unionKey)
                let finalForMM = Math.max(
                    longPosForMM.add(longOrder).toNumber(),
                    shortPosForMM.add(shortOrder).toNumber()
                )
                const contractConfig = getContractConfig(contractList, item)

                const contractLevelList = get(ContractPositionLevelList(contractConfig.symbolId))
                let levelInfo = getLevel(contractLevelList, finalForMM)
                let feeRate = contractConfig.feeRate
                let mm = levelInfo.keepMarginRate
                let fee = new Decimal(final)
                    .times(new Decimal(mm).add(new Decimal(feeRate)))
                    .toNumber()
                tokenIdTotalValue.add(tokenId, fee)
                tokenIdTotalValueMax.add(unionKey, final)

                let allLongPos = symbolIdTotalFixedLongPosValue.get(unionKey)
                let allShortPos = symbolIdTotalFixedShortPosValue.get(unionKey)
                let allLongOrderPos = symbolIdTotalFixedLongOrderValue.get(unionKey)
                let allShortOrderPos = symbolIdTotalFixedShortOrderValue.get(unionKey)
                //币对当前价值=max(多头仓位价值+多头委托价值，空头仓位价值+空头委托价值)
                final = Math.max(
                    allLongPos.add(allLongOrderPos).toNumber(),
                    allShortPos.add(allShortOrderPos).toNumber()
                )
                // 币对占用价值
                symbolIdTotalAllMaxValue.set(unionKey, final)
            }
        })

        return {
            symbolIdTotalLongOrderMargin,
            symbolIdTotalShortOrderMargin,
            symbolIdTotalLongPosMargin,
            symbolIdTotalShortPosMargin,
            symbolIdCurTotalLongPosMargin,
            symbolIdCurTotalShortPosMargin,
            symbolIdTotalAllMaxValue,
            symbolIdTotalMargin,
            tokenIdFixedTotalValue,
            tokenIdTotalValue,
            tokenIdTotalMargin,
            tokenIdFixedTotalMargin,
            symbolIdTotalLongPosNum,
            symbolIdTotalShortPosNum
        }
    }
})

/**
 * //单向持仓 逐仓的币对维持保证金额  max(逐仓多头委托价值，逐仓空头委托价值)*(MM+爆仓手续费) 这个好像没用。。
 */
export const FixedPosMaintainValueOneWaySelector = selector({
    key: 'fixed-pos-maintain-value-oneway-selector',
    get: ({ get }) => {
        const positionList = get(MixContractBaseFixedPositionListSelector)
        const contractList = get(CurrentBusinessLineContractListState)
        const { tokenIdFixedTotalValue, symbolIdTotalAllMaxValue } = get(
            MixOneWayCalculateOrdersMargin
        )
        const curTargetOrder = get(MixContractCurrentOrdersState) // 当前委托 普通委托  没有 planType 字段
        //所有的委托 包含止盈止损委托 不区分全仓逐仓
        let allOrderList: MixContractCurrentOrderFixedData[] = formatCurrentOrders(
            get,
            curTargetOrder
        )
        const tokenTotalFixedPosOneWayMaintainValue = new StatisticResult()
        positionList
            .filter(o => o.marginMode === MixContractMarginMode.FIXED_MARGIN)
            .forEach(o => {
                const { symbolId, tokenId } = o
                const unionKey = `${tokenId}_${symbolId}`
                const contractConfig = getContractConfig(contractList, symbolId)
                // 逐仓的币对维持保证金额 = max(逐仓多头委托价值，逐仓空头委托价值)*(MM+爆仓手续费)
                // max(逐仓多头委托价值，逐仓空头委托价值)
                let maxFixedOrderValue = tokenIdFixedTotalValue.get(tokenId)
                //这里应该用所有仓位价值的大头去取对应的mm
                let curValue = symbolIdTotalAllMaxValue.get(unionKey)
                const feeRate = contractConfig.feeRate
                const contractLevelList = get(ContractPositionLevelList(contractConfig.symbolId))
                // 查询仓位档位
                const levelInfo = getLevel(contractLevelList, curValue.toNumber())
                let mm = levelInfo.keepMarginRate

                let rate = new Decimal(maxFixedOrderValue)
                    .times(new Decimal(mm).add(new Decimal(feeRate)))
                    .toNumber()
                tokenTotalFixedPosOneWayMaintainValue.add(tokenId, rate)
            })
        allOrderList.forEach(item => {
            // 尝试找到对应的仓位
            const { symbolId, tokenId } = item
            const position = positionList.find(p => {
                if (p.symbolId === symbolId && p.tokenId === tokenId) {
                    return true
                } else {
                    return false
                }
            })
            //仓位里面有 就说明上面计算过了 这里就不再计算了
            if (!position) {
                const unionKey = `${tokenId}_${symbolId}`
                const contractConfig = getContractConfig(contractList, symbolId)
                // 逐仓的币对维持保证金额 = max(逐仓多头委托价值，逐仓空头委托价值)*(MM+爆仓手续费)
                // max(逐仓多头委托价值，逐仓空头委托价值)
                let maxFixedOrderValue = tokenIdFixedTotalValue.get(tokenId)
                //这里应该用所有仓位价值的大头去取对应的mm
                let curValue = symbolIdTotalAllMaxValue.get(unionKey)
                const feeRate = contractConfig.feeRate
                const contractLevelList = get(ContractPositionLevelList(contractConfig.symbolId))
                // 查询仓位档位
                const levelInfo = getLevel(contractLevelList, curValue.toNumber())
                let mm = levelInfo.keepMarginRate

                let rate = new Decimal(maxFixedOrderValue)
                    .times(new Decimal(mm).add(new Decimal(feeRate)))
                    .toNumber()
                tokenTotalFixedPosOneWayMaintainValue.add(tokenId, rate)
            }
        })
        return tokenTotalFixedPosOneWayMaintainValue
    }
})
